import React, { useState } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faCheckCircle,
  faTimesCircle,
  faCoffee,
  faPuzzlePiece,
  faSlidersH,
  faRuler,
  faClock,
  faBoxes,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { getCookies } from "../../hooks/useCookies";
import { REACT_APP_API_REFRESH_POS_ITEMS } from "../../redux/apiConstants";
import styles from "./RefreshPosItemsModal.module.scss";
import Loader from '../Loader/Loader';

const RefreshPosItemsModal = ({ show, handleClose, cafeId, cafeName }) => {
  const [modalState, setModalState] = useState("confirmation");
  const [syncResult, setSyncResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const token = `Bearer ${getCookies("Token")}`;

  const handleRefresh = async () => {
    setModalState("loading");
    setErrorMessage("");

    try {
      const response = await axios.post(
        `${REACT_APP_API_REFRESH_POS_ITEMS}/${cafeId}`,
        {},
        {
          headers: { Authorization: token },
          timeout: 0,
        }
      );

      if (response.data.success || response.data.code === 200) {
        setSyncResult(response.data.data?.result || response.data.data);
        setModalState("success");
      } else {
        setErrorMessage(
          response.data.message || "An error occurred while refreshing items."
        );
        setModalState("error");
      }
    } catch (error) {
      let message = "An unexpected error occurred. Please try again.";

      if (error.response) {
        if (typeof error.response.data === "string" && error.response.data.includes("<html")) {
          message = "The server took too long to respond. Please try again.";
        } else if (error.response.data?.message) {
          message = error.response.data.message;
        }
      } else if (error.code === "ECONNABORTED") {
        message = "The request was interrupted. Please try again.";
      } else if (!error.response) {
        message = "Unable to connect to the server. Please check your connection.";
      }

      setErrorMessage(message);
      setModalState("error");
    }
  };

  const handleCloseModal = () => {
    setModalState("confirmation");
    setSyncResult(null);
    setErrorMessage("");
    handleClose();
  };

  const handleTryAgain = () => {
    setModalState("confirmation");
    setErrorMessage("");
  };

  const renderConfirmation = () => (
    <>
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <FontAwesomeIcon icon={faSync} className={styles.headerIcon} />
          Refresh POS Items
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.cafeInfoCard}>
          <h4 className={styles.cafeName}>{cafeName}</h4>
          <div className={styles.posBadge}>
            <span className={styles.squareBadge}>Square POS</span>
            <span className={styles.connectedText}>Connected</span>
          </div>
        </div>

        <div className={styles.sectionCard}>
          <h5 className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>📋</span>
            What Will Be Refreshed
          </h5>
          <div className={styles.dataGrid}>
            <div className={styles.dataItem}>
              <FontAwesomeIcon icon={faCoffee} className={styles.dataIcon} />
              <div className={styles.dataContent}>
                <span className={styles.dataLabel}>Menu Items</span>
                <span className={styles.dataDesc}>All drinks & beverages</span>
              </div>
            </div>
            <div className={styles.dataItem}>
              <FontAwesomeIcon icon={faPuzzlePiece} className={styles.dataIcon} />
              <div className={styles.dataContent}>
                <span className={styles.dataLabel}>Addons</span>
                <span className={styles.dataDesc}>Modifiers & extras</span>
              </div>
            </div>
            <div className={styles.dataItem}>
              <FontAwesomeIcon icon={faSlidersH} className={styles.dataIcon} />
              <div className={styles.dataContent}>
                <span className={styles.dataLabel}>Options</span>
                <span className={styles.dataDesc}>Sizes, milk types, etc.</span>
              </div>
            </div>
            <div className={styles.dataItem}>
              <FontAwesomeIcon icon={faRuler} className={styles.dataIcon} />
              <div className={styles.dataContent}>
                <span className={styles.dataLabel}>Units</span>
                <span className={styles.dataDesc}>Measurement units</span>
              </div>
            </div>
            <div className={styles.dataItem}>
              <FontAwesomeIcon icon={faClock} className={styles.dataIcon} />
              <div className={styles.dataContent}>
                <span className={styles.dataLabel}>Business Hours</span>
                <span className={styles.dataDesc}>Operating schedule</span>
              </div>
            </div>
            <div className={styles.dataItem}>
              <FontAwesomeIcon icon={faBoxes} className={styles.dataIcon} />
              <div className={styles.dataContent}>
                <span className={styles.dataLabel}>Inventory</span>
                <span className={styles.dataDesc}>Stock levels</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.warningCard}>
          <h5 className={styles.warningTitle}>
            <FontAwesomeIcon icon={faExclamationTriangle} className={styles.warningIcon} />
            Important Information
          </h5>
          <ul className={styles.warningList}>
            <li>
              <span className={styles.warningEmoji}>⏱️</span>
              This process may take several minutes depending on the number of menu items.
            </li>
            <li>
              <span className={styles.warningEmoji}>🔄</span>
              All existing POS items will be updated with the latest data from Square.
            </li>
            <li>
              <span className={styles.warningEmoji}>⭐</span>
              Customer reviews and ratings will be preserved.
            </li>
            <li>
              <span className={styles.warningEmoji}>🚫</span>
              Please DO NOT close this window or navigate away during the sync process.
            </li>
          </ul>
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Button
          variant="outline-secondary"
          onClick={handleCloseModal}
          className={styles.cancelBtn}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleRefresh}
          className={styles.refreshBtn}
        >
          <FontAwesomeIcon icon={faSync} className={styles.btnIcon} />
          Start Refresh
        </Button>
      </Modal.Footer>
    </>
  );

  const renderLoading = () => (
    <>
      <Modal.Body className={styles.loadingBody}>
        <div className={styles.loadingContainer}>
          <Loader />
          <h4 className={styles.loadingTitle}>Syncing Items from Square...</h4>
          <p className={styles.loadingSubtitle}>
            This may take a few minutes. Please wait...
          </p>
          <p className={styles.loadingWarning}>
            Do not close this window.
          </p>
        </div>
      </Modal.Body>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Body className={styles.successBody}>
        <div className={styles.successContainer}>
          <div className={styles.successIconWrapper}>
            <FontAwesomeIcon icon={faCheckCircle} className={styles.successIcon} />
          </div>
          <h4 className={styles.successTitle}>Sync Complete!</h4>
          <p className={styles.successSubtitle}>
            All items have been refreshed from Square.
          </p>

          {syncResult && (
            <div className={styles.statsCard}>
              <h5 className={styles.statsTitle}>Sync Summary</h5>
              <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                  <span className={styles.statValue}>{syncResult.drinksCount || 0}</span>
                  <span className={styles.statLabel}>Menu Items</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statValue}>{syncResult.addonsCount || 0}</span>
                  <span className={styles.statLabel}>Addons</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statValue}>{syncResult.optionsCount || 0}</span>
                  <span className={styles.statLabel}>Options</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statValue}>{syncResult.unitsCount || 0}</span>
                  <span className={styles.statLabel}>Units</span>
                </div>
              </div>
              <div className={styles.hoursStatus}>
                <span className={styles.hoursLabel}>Business Hours:</span>
                <span className={syncResult.hoursUpdated ? styles.hoursUpdated : styles.hoursNoChange}>
                  {syncResult.hoursUpdated ? "Updated" : "No Changes"}
                </span>
              </div>
              {syncResult.syncedAt && (
                <p className={styles.syncTime}>
                  Synced at: {new Date(syncResult.syncedAt).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Button
          variant="primary"
          onClick={handleCloseModal}
          className={styles.closeBtn}
        >
          Close
        </Button>
      </Modal.Footer>
    </>
  );

  const renderError = () => (
    <>
      <Modal.Body className={styles.errorBody}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIconWrapper}>
            <FontAwesomeIcon icon={faTimesCircle} className={styles.errorIcon} />
          </div>
          <h4 className={styles.errorTitle}>Sync Failed</h4>
          <div className={styles.errorMessageBox}>
            <p className={styles.errorMessage}>{errorMessage}</p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Button
          variant="outline-secondary"
          onClick={handleCloseModal}
          className={styles.cancelBtn}
        >
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleTryAgain}
          className={styles.retryBtn}
        >
          <FontAwesomeIcon icon={faSync} className={styles.btnIcon} />
          Try Again
        </Button>
      </Modal.Footer>
    </>
  );

  const renderContent = () => {
    switch (modalState) {
      case "loading":
        return renderLoading();
      case "success":
        return renderSuccess();
      case "error":
        return renderError();
      default:
        return renderConfirmation();
    }
  };

  return (
    <Modal
      show={show}
      onHide={modalState === "loading" ? undefined : handleCloseModal}
      centered
      size="lg"
      backdrop={modalState === "loading" ? "static" : true}
      keyboard={modalState !== "loading"}
      className={styles.refreshModal}
    >
      {renderContent()}
    </Modal>
  );
};

export default RefreshPosItemsModal;
